import { connect, Field } from 'formik'
import { every, get, isUndefined, omit, omitBy, size } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { FormattedMessage, useIntl } from 'react-intl'
import Toggle from 'react-toggle'

import Button from '@/components/Button'
import Form, { Select, Text } from '@/components/Form'
import ImagePreview from '@/components/ImagePreview'

import { appCatalogFormSchema } from './formSchema'
import messages from './messages'
import WebHookDisplay from './WebHookDisplay'

import './index.scss'

import Header from '@/components/Card/Header'
import DestructiveConfirmationOverlay from '@/components/DestructiveConfirmationOverlay'
import AudienceTargeting from '@/components/Form/AudienceTargeting'
import Checkbox from '@/components/Form/Checkbox'
import ValidationWarning from '@/components/Form/ValidationWarning'
import { ImageLibraryWithFormik } from '@/components/ImageLibrary'

const CatalogAppInstanceForm = ({
  actionMessage,
  app,
  appImageUrlOverride,
  appNameOverride,
  goBack,
  isActionInProgress,
  isUpdateMode,
  onDelete,
  onInstall,
  onUpdate,
  webHookUrl,
  goToConnectorLogs
}: any) => {
  const intl = useIntl()

  const [isAudienceTargetingExpanded, setIsAudienceTargetingExpanded] = useState(false)
  const [isSmartNotificationsExpanded, setIsSmartNotificationsExpanded] = useState(false)
  const [areNameAndIconCustom, setAreNameAndIconCustom] = useState(false)
  const [isAppUserInterfaceOn, setisAppUserInterfaceOn] = useState(false)
  const [toggleSubcomponentOff, setToggleSubcomponentOff] = useState(false)
  const [tempTarget, setTempTarget] = useState<any>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onCancelImageCallback = useRef()

  const pollingSliderSteps = [
    intl.formatMessage(messages.polling1MinuteDisplay),
    intl.formatMessage(messages.polling30MinuteDisplay),
    intl.formatMessage(messages.polling1HourDisplay),
    intl.formatMessage(messages.polling6HourDisplay),
    intl.formatMessage(messages.polling12HourDisplay),
    intl.formatMessage(messages.polling24HourDisplay)
  ]
  const pollingSliderStepsMinutes = {
    0: '1',
    1: '30',
    2: '60',
    3: '360',
    4: '720',
    5: '1440'
  }

  useEffect(() => {
    const iconUrl = app ? app.iconUrl : ''

    if ((appImageUrlOverride && appImageUrlOverride !== iconUrl) || appNameOverride) {
      setAreNameAndIconCustom(true)
    }

    if (isUpdateMode && app.connectorId) {
      setIsSmartNotificationsExpanded(true)
    }
    if (isUpdateMode && app.microAppId) {
      setisAppUserInterfaceOn(true)
    }

    if (size(app.audience) !== 0 && app.audience[0].filters) {
      setIsAudienceTargetingExpanded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generateConfigFields = (formik: any) => {
    const config = app?.config
    const properties = get(config, 'properties', config)
    const requiredFields = get(config, 'required', [])
    const sensitiveFields = get(config, 'sensitiveFields', [])

    if (!config) {
      return <FormattedMessage {...messages.comingSoon} />
    }

    return (
      <div className="config-fields">
        {Object.keys(config.properties || []).map(fieldName => {
          const field = properties[fieldName]
          const typeValue = get(field, 'format', '').toLowerCase() === 'email' ? 'email' : 'text'
          const options =
            field.enum &&
            // @ts-expect-error
            field.enum.map((val, index) => ({
              label: field.enumNames && field.enumNames[index] ? field.enumNames[index] : val,
              value: val
            }))
          const checkbox = field.enum
            ? (field.enum.includes('true') || field.enum.includes('false')) && !requiredFields.includes(fieldName)
            : false
          const fieldType = options ? 'select' : 'text'

          switch (fieldType) {
            case 'select': {
              return checkbox ? (
                <Checkbox.Input
                  id={fieldName}
                  name={fieldName}
                  value="true"
                  key={fieldName}
                  label={properties[fieldName].title}
                  onChange={(e: any) => setFieldValueForCatalogCheckbox(e, formik, fieldName)}
                />
              ) : (
                <Select
                  disabled={isUpdateMode && properties[fieldName].immutable}
                  key={fieldName}
                  label={properties[fieldName].title}
                  name={fieldName}
                  required={requiredFields.includes(fieldName)}
                  options={options}
                />
              )
            }

            default: {
              return (
                <Text
                  key={fieldName}
                  label={properties[fieldName].title}
                  name={fieldName}
                  type={sensitiveFields.includes(fieldName) ? 'password' : typeValue}
                  required={requiredFields.includes(fieldName)}
                />
              )
            }
          }
        })}
      </div>
    )
  }

  const generateInitialData = () => {
    const properties = get(app, 'config.properties', app.config)
    const customData = get(app, 'customData', [])
    const audienceTargeting = get(app, 'audience', [])
    const configuredPollingInterval = get(app, 'connectorConfig.pollingInterval')
    const catalogPresetPollingInterval = get(app, 'pollingInterval') // Polling interval value for apps where the interval is static for all instances
    // Here we are just creating an 'unsigned' version of appImageUrlOverride for use below
    let unsignedUrl = ''
    if (appImageUrlOverride !== '') {
      const parsedUrl = new URL(appImageUrlOverride)
      unsignedUrl = parsedUrl.origin + parsedUrl.pathname
    }
    const appValues = properties
      ? Object.keys(properties).reduce((initialValues, field) => {
          const defaultValue = get(properties[field], 'enum[0]', properties[field].default || '')
          const customValue = get(customData, `${field}`)
          // @ts-expect-error
          initialValues[field] = customValue || defaultValue
          return initialValues
        }, {})
      : []
    const catalogName = app ? app.name : ''
    const catalogImage = app ? app.iconUrl : ''
    const pollingIntervalKey = configuredPollingInterval
      ? Object.keys(pollingSliderStepsMinutes).find(
          // @ts-expect-error
          step => Number.parseInt(pollingSliderStepsMinutes[step]) === configuredPollingInterval
        )
      : 0
    return {
      ...appValues,
      audienceTargeting: size(audienceTargeting) !== 0 && audienceTargeting[0].filters ? audienceTargeting : [],
      // We need both appImageOverride and appImageOverrideSigned so the ImageUpload component renders correctly
      appImageOverride: appImageUrlOverride === catalogImage && catalogImage !== '' ? '' : unsignedUrl,
      appImageOverrideSigned: appImageUrlOverride === catalogImage && catalogImage !== '' ? '' : appImageUrlOverride,
      appNameOverride: appNameOverride === catalogName && catalogName !== '' ? '' : appNameOverride,
      // @ts-expect-error
      pollingInterval: catalogPresetPollingInterval || pollingSliderStepsMinutes[pollingIntervalKey],
      customizeNameAndIcon: ['true']
    }
  }

  const setFieldValueForCatalogCheckbox = (e: any, formik: any, name: any) => {
    const { checked } = e.target
    formik.setFieldValue(name, checked ? 'true' : 'false')
  }

  const omitValues = (values: any) => {
    return omit(values, ['customizeNameAndIcon'])
  }

  const submitUpdate = (values: any) => {
    const newValues = omitValues(values)
    const { appImageOverride, appNameOverride, audienceTargeting, pollingInterval, ...rest } = newValues
    let audienceValue = audienceTargeting
    if (!audienceValue || size(audienceValue) === 0 || !isAudienceTargetingExpanded) {
      if (isAppUserInterfaceOn) {
        audienceValue = [
          {
            type: 'filter',
            value: 'active eq true'
          }
        ]
      } else {
        audienceValue = null
      }
    }
    let updatedApp: any = {
      audience: audienceValue,
      overrideAppName: '',
      overrideAppIconUrl: '',
      catalogId: app.catalogId,
      installConnector: false,
      installMicroApp: false,
      config: {
        ...rest
      },
      connectorId: get(app, 'connectorId'),
      microAppId: get(app, 'microAppId')
    }

    if (isSmartNotificationsExpanded) {
      updatedApp = {
        ...updatedApp,
        connectorConfig: {
          pollingInterval: app.eventModel === 'p' ? Number.parseInt(pollingInterval) : null
        }
      }
      if (!app.connectorId) {
        updatedApp = {
          ...updatedApp,
          installConnector: true
        }
      }
    }

    if (isAppUserInterfaceOn) {
      if (app.microAppId) {
        updatedApp = {
          ...updatedApp,
          microAppConfig: get(app, 'microAppConfig')
        }
      } else {
        updatedApp = {
          ...updatedApp,
          installMicroApp: true
        }
      }
    }

    if (areNameAndIconCustom) {
      updatedApp = {
        ...updatedApp,
        overrideAppName: appNameOverride || '',
        overrideAppIconUrl: appImageOverride || ''
      }
    }

    /*
      Removing any undefined ids, whenever
      connectorId or microAppId don't exist
    */
    const cleanUpdatedApp = omitBy(updatedApp, isUndefined)
    onUpdate(app.id, cleanUpdatedApp)
  }

  const submitInstall = (values: any) => {
    const newValues = omitValues(values)
    const { appImageOverride, appNameOverride, audienceTargeting, pollingInterval, ...rest } = newValues
    let audienceValue = audienceTargeting
    if (!audienceValue || size(audienceValue) === 0 || !isAudienceTargetingExpanded) {
      if (isAppUserInterfaceOn) {
        audienceValue = [
          {
            type: 'filter',
            value: 'active eq true'
          }
        ]
      } else {
        audienceValue = null
      }
    }
    let appToInstall: any = {
      audience: audienceValue,
      overrideAppName: '',
      overrideAppIconUrl: '',
      catalogId: app.id,
      installConnector: false,
      installMicroApp: false,
      config: {
        ...rest
      }
    }
    if (isSmartNotificationsExpanded) {
      appToInstall = {
        ...appToInstall,
        installConnector: true,
        connectorConfig: {
          pollingInterval: app.eventModel === 'p' ? Number.parseInt(pollingInterval) : null
        }
      }
    }
    if (isAppUserInterfaceOn) {
      appToInstall = {
        ...appToInstall,
        installMicroApp: true
      }
    }
    if (areNameAndIconCustom) {
      appToInstall = {
        ...appToInstall,
        overrideAppName: appNameOverride || '',
        overrideAppIconUrl: appImageOverride || ''
      }
    }
    onInstall(appToInstall)
  }

  const onSubmit = (values: any) => {
    if (!isSubmitting) {
      setIsSubmitting(true)
      return isUpdateMode ? submitUpdate(values) : submitInstall(values)
    }
  }

  const sectionExpandedHandleChange = (e: any) => {
    const isExpanded = e.target.value !== 'true'
    const toggleName = e.target.name
    const subcomponentExists =
      (toggleName === 'isSmartNotificationsExpanded' && app.connectorId) ||
      (toggleName === 'isAppUserInterfaceOn' && app.microAppId)
    if (!isExpanded && subcomponentExists) {
      setToggleSubcomponentOff(true)
      setTempTarget(e.target)
    } else {
      sectionExpandedHandleConfirm(e)
    }
  }

  const sectionExpandedHandleConfirm = (e: any) => {
    const target = e.target.name ? e.target : tempTarget
    setTempTarget({})
    const toggleName = target.name
    const isExpanded = target.value !== 'true'

    switch (toggleName) {
      case 'isSmartNotificationsExpanded': {
        setIsSmartNotificationsExpanded(isExpanded)
        break
      }
      case 'isAppUserInterfaceOn': {
        setisAppUserInterfaceOn(isExpanded)
        break
      }
      case 'isAudienceTargetingExpanded': {
        {
          setIsAudienceTargetingExpanded(isExpanded)
          // No default
        }
        break
      }
    }

    if (toggleSubcomponentOff) {
      undoSubcomponentToggleOff()
    }
  }

  const undoSubcomponentToggleOff = () => setToggleSubcomponentOff(false)

  const toggleDefaults = () => setAreNameAndIconCustom(!areNameAndIconCustom)

  const registerOnCancel = (cancelCallback: any) => {
    onCancelImageCallback.current = cancelCallback
  }

  const back = () => {
    // First ask the image input to do what it needs because we are canceling!

    onCancelImageCallback.current
    goBack()
  }

  const appName = app ? app.name : ''
  const config = get(app, 'config', {})
  const initialCheckbox = !!(app.overrideAppName || app.overrideAppIconUrl)
  let audienceTitle = ''

  if (isAudienceTargetingExpanded) {
    if (size(app.overrideAppName) > 0) {
      audienceTitle = app.overrideAppName
    } else {
      audienceTitle = appName
    }
  }

  const buttonCancelText = intl.formatMessage(isUpdateMode ? messages.cancel : messages.back)
  const buttonDeleteText = isUpdateMode ? intl.formatMessage(messages.delete) : ''
  const buttonUpdateText = intl.formatMessage(isUpdateMode ? messages.update : messages.install)

  return (
    app && (
      <Form
        actionMessage={actionMessage}
        buttons={
          <>
            <Button
              color="secondary"
              className="app-details-button"
              type="button"
              onClick={back}
              title={buttonCancelText}
            >
              {buttonCancelText}
            </Button>
            {isUpdateMode && (
              <Button color="warning" type="button" onClick={onDelete} title={buttonDeleteText}>
                {buttonDeleteText}
              </Button>
            )}
            <Button
              color="primary"
              type="submit"
              id="catalogAppAction"
              disabled={isSubmitting}
              title={buttonUpdateText}
            >
              {buttonUpdateText}
            </Button>
          </>
        }
        className="install-app"
        initialValues={generateInitialData()}
        isActionInProgress={isActionInProgress}
        onReset={back}
        onSubmit={onSubmit}
        schema={appCatalogFormSchema(config, { isAudienceTargetingExpanded, isSmartNotificationsExpanded })}
        validationWarning={<ValidationWarning message={messages.validationWarning} />}
        title={
          <>
            <div className="ref-guide-link">
              <a
                href="https://help.workgrid.com/s/article/Install-a-Catalog-App"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage {...messages.referenceGuide} />
              </a>
            </div>
            <div className="flex">
              <ImagePreview
                className="icon-preview-upload"
                id="appIcon"
                imageUrl={app.overrideAppIconUrl || app.iconUrl}
                title=""
              />
              <div className="install-app-card-title">
                <Header>{appName}</Header>
                <p className="install-app-card-subtitle">
                  <FormattedMessage
                    {...messages.headerSubtitle}
                    values={{ appName: appName, spaceName: 'the current space' }}
                  />
                </p>
              </div>
            </div>
          </>
        }
      >
        {/* @ts-expect-error */}
        <FormInternal>
          {(formik: any) => {
            return (
              <>
                <Header
                  className="form-section-header"
                  title={<FormattedMessage {...messages.settingsAccessAudience} />}
                />
                <div className="settings-access-block">
                  {generateConfigFields(formik)}
                  <Checkbox name="customize-name-and-icon">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="field__label">
                      <FormattedMessage {...messages.internalBrandingOverride} />
                    </label>
                    <Checkbox.Input
                      id="customize-name-and-icon"
                      name="customizeNameAndIcon"
                      value={`${initialCheckbox}`}
                      key="customizeNameAndIcon"
                      label={<FormattedMessage {...messages.customizeNameAndIcon} />}
                      onChange={toggleDefaults}
                    />
                    <AnimateHeight
                      className="name-and-icon"
                      duration={50}
                      height={areNameAndIconCustom ? 'auto' : 0}
                      animateOpacity
                    >
                      <Text
                        name="appNameOverride"
                        label={<FormattedMessage {...messages.customAppName} />}
                        placeholder={intl.formatMessage(messages.customAppNamePlaceholder)}
                        maxLength={128}
                      />
                      {/* @ts-expect-error */}
                      <ImageLibraryWithFormik
                        label={intl.formatMessage(messages.customAppIcon)}
                        name="appImageOverride"
                        placeholder={intl.formatMessage(messages.customAppIconPlaceholder)}
                        cancel={registerOnCancel}
                      />
                    </AnimateHeight>
                  </Checkbox>
                </div>
                <Header title={<FormattedMessage {...messages.functionalityFeatures} />} />
                <div className="app-form-fields">
                  {app.isMicroAppAvailable && (
                    <div id="AudienceTargetingSettings" className="app-component">
                      <div className="additional-fields__row accordion-title">
                        <FormattedMessage {...messages.audienceTargeting} />
                        <Toggle
                          checked={isAudienceTargetingExpanded}
                          name="isAudienceTargetingExpanded"
                          value={String(isAudienceTargetingExpanded)}
                          onChange={sectionExpandedHandleChange}
                          aria-label={intl.formatMessage(messages.audienceTargeting)}
                        />
                      </div>
                      <div className="additional-fields-summary__label columnHeader">
                        {!isAudienceTargetingExpanded && (
                          <>
                            <span className="subcomponent-text">
                              <FormattedMessage {...messages.audienceTargetingDescription} />
                            </span>
                            <span>
                              <FormattedMessage {...messages.audienceTargetingSubComponentDescription} />
                            </span>
                          </>
                        )}
                      </div>
                      <Field name="audienceTargeting">
                        {({ form: { setFieldError } }: any) => {
                          return (
                            <AnimateHeight
                              className="form-section"
                              duration={500}
                              height={isAudienceTargetingExpanded ? 'auto' : 0}
                              animateOpacity
                              onAnimationEnd={() => {
                                if (!isAudienceTargetingExpanded) setFieldError('audienceTargeting', undefined)
                              }}
                            >
                              <AudienceTargeting
                                name="audienceTargeting"
                                title={audienceTitle}
                                startWithAudience={true}
                                firstFilterMandatory={true}
                              />
                            </AnimateHeight>
                          )
                        }}
                      </Field>
                    </div>
                  )}
                  {app.isConnectorAvailable && (
                    <div id="SmartNotificationsSettings" className="app-component">
                      <div className="additional-fields__row accordion-title">
                        <FormattedMessage {...messages.smartNotifications} />
                        <Toggle
                          checked={isSmartNotificationsExpanded}
                          name="isSmartNotificationsExpanded"
                          value={String(isSmartNotificationsExpanded)}
                          onChange={sectionExpandedHandleChange}
                          aria-label={intl.formatMessage(messages.smartNotifications)}
                        />
                        {!!toggleSubcomponentOff && tempTarget.name === 'isSmartNotificationsExpanded' && (
                          <DestructiveConfirmationOverlay
                            onConfirm={sectionExpandedHandleConfirm}
                            onCancel={undoSubcomponentToggleOff}
                            onConfirmActionTitle={<FormattedMessage {...messages.disableConfirm} />}
                            message={<FormattedMessage {...messages.disableCheck} />}
                            subMessage={<FormattedMessage {...messages.disablingSmartNotifications} />}
                            confirmIsPrimary={true}
                          />
                        )}
                      </div>

                      <div className="additional-fields-summary__label connector-columnHeader">
                        <div className="connector-text-container">
                          <span className="subcomponent-text">
                            <FormattedMessage {...messages.smartNotificationsDescription} />
                          </span>
                          {isSmartNotificationsExpanded ? (
                            <span className="subcomponent-on-text">
                              <FormattedMessage {...messages.subcomponentDescriptionOn} />
                            </span>
                          ) : (
                            <FormattedMessage {...messages.subcomponentDescriptionOff} />
                          )}
                        </div>
                        {isSmartNotificationsExpanded && app.connectorId && (
                          <div className="connector-logs">
                            <Button
                              color="tertiary"
                              className="view-logs-button form__field field__label tertiary_button"
                              onClick={goToConnectorLogs}
                            >
                              <FormattedMessage {...messages.logs} />
                            </Button>
                          </div>
                        )}
                      </div>

                      <AnimateHeight
                        className="form-section"
                        duration={500}
                        height={isSmartNotificationsExpanded ? 'auto' : 0}
                        animateOpacity
                      >
                        <div className="connector-fields">
                          {app.eventModel === 'p' && !app.pollingInterval && (
                            <Select
                              className="shortened_select"
                              key="pollingInterval"
                              label={<FormattedMessage {...messages.pollingInterval} />}
                              name="pollingInterval"
                              tooltip={<FormattedMessage {...messages.pollingTooltip} />}
                              required
                            >
                              {pollingSliderSteps.map((pollingOption, i) => {
                                return (
                                  // @ts-expect-error
                                  <Select.Option key={i} value={pollingSliderStepsMinutes[i]}>
                                    {pollingOption}
                                  </Select.Option>
                                )
                              })}
                            </Select>
                          )}
                          {every([app.eventModel !== 'p', isUpdateMode, webHookUrl]) && (
                            <WebHookDisplay webHookUrl={webHookUrl} />
                          )}
                        </div>
                      </AnimateHeight>
                    </div>
                  )}
                  {app.isMicroAppAvailable && (
                    <div id="AppUiSettings" className="app-component">
                      <div className="additional-fields__row accordion-title">
                        <FormattedMessage {...messages.appUserInterface} />
                        <Toggle
                          checked={isAppUserInterfaceOn}
                          name="isAppUserInterfaceOn"
                          value={String(isAppUserInterfaceOn)}
                          onChange={sectionExpandedHandleChange}
                          aria-label={intl.formatMessage(messages.appUserInterface)}
                        />
                        {!!toggleSubcomponentOff && tempTarget.name === 'isAppUserInterfaceOn' && (
                          <DestructiveConfirmationOverlay
                            onConfirm={sectionExpandedHandleConfirm}
                            onCancel={undoSubcomponentToggleOff}
                            onConfirmActionTitle={<FormattedMessage {...messages.disableConfirm} />}
                            message={<FormattedMessage {...messages.disableCheck} />}
                            subMessage={<FormattedMessage {...messages.disablingAppUserInterface} />}
                            confirmIsPrimary={true}
                          />
                        )}
                      </div>
                      <div className="additional-fields-summary__label columnHeader">
                        <span className="subcomponent-text">
                          <FormattedMessage {...messages.appUserInterfaceDescription} />
                        </span>
                        {isAppUserInterfaceOn ? (
                          <span className="subcomponent-on-text">
                            <FormattedMessage {...messages.subcomponentDescriptionOn} />
                          </span>
                        ) : (
                          <FormattedMessage {...messages.subcomponentDescriptionOff} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )
          }}
        </FormInternal>
      </Form>
    )
  )
}

// Functions as children wrapper - used to inject formik object into parent Form component
// @ts-expect-error
const FormInternal = connect(({ children, formik }) => {
  return children(formik)
})
CatalogAppInstanceForm.displayName = 'CatalogAppInstanceForm'

CatalogAppInstanceForm.propTypes = {
  actionMessage: PropTypes.element,
  app: PropTypes.object.isRequired,
  appImageUrlOverride: PropTypes.string,
  appNameOverride: PropTypes.string,
  goBack: PropTypes.func.isRequired,

  isActionInProgress: PropTypes.bool.isRequired,
  isUpdateMode: PropTypes.bool,
  onDelete: PropTypes.func,
  onInstall: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  webHookUrl: PropTypes.string,
  goToConnectorLogs: PropTypes.func
}

CatalogAppInstanceForm.defaultProps = {
  actionMessage: null,
  appNameOverride: '',
  isUpdateMode: false,
  appImageUrlOverride: '',
  onDelete: () => {},
  webHookUrl: '',
  isActionInProgress: false,
  goToConnectorLogs: () => {}
}

export default CatalogAppInstanceForm
